import moment from "moment";

export const PERIODS = Array.from({ length: 8 }, (_, i) => i + 1);
export const EMPTY_LIST = [];
export const EMPTY_OBJECT = {};
export const NO_RECORDS_MESSAGE = "No Records found!";
export const DATE_KEY_FORMAT = "YYYY-MM-DD";

export const toDateKey = (date) => moment(date).format(DATE_KEY_FORMAT);

export const toWeekKey = (date) => {
  const m = moment(date);
  return `${m.isoWeekYear()}-${m.isoWeek()}`;
};

// ISO weekday: 1 = Monday ... 7 = Sunday (same numbering as the API's dayid)
const getItemWeekday = (item) =>
  Number(item.dayid) || moment(item.date, DATE_KEY_FORMAT).isoWeekday();

/**
 * Groups weekly API rows as { [isoWeekday]: { [period]: item } } for the week of `date`.
 *
 * The API filters by ISO week number only (weekend_id has no year), so it can
 * return rows dated in the same week of another year. When the response has
 * rows inside the selected week those exact dates are used; otherwise the rows
 * are mapped onto the selected week by weekday.
 */
export const groupWeekByDay = (data, date) => {
  if (!data?.length) return EMPTY_OBJECT;

  const weekStart = moment(date).startOf("isoWeek");
  const startKey = weekStart.format(DATE_KEY_FORMAT);
  const endKey = weekStart.clone().add(6, "days").format(DATE_KEY_FORMAT);

  const inWeek = data.filter(
    (item) => item.date >= startKey && item.date <= endKey,
  );
  const rows = inWeek.length > 0 ? inWeek : data;

  return rows.reduce((acc, item) => {
    const weekday = getItemWeekday(item);
    const periods = acc[weekday] || (acc[weekday] = {});
    const existing = periods[item.period];
    // Several years can share a week number – keep the most recent entry
    if (!existing || item.date >= existing.date) {
      periods[item.period] = item;
    }
    return acc;
  }, {});
};

export const getDayRows = (byDay, date) =>
  Object.values(byDay[moment(date).isoWeekday()] || EMPTY_OBJECT).sort(
    (a, b) => a.period - b.period,
  );

// Convert school-hour time (6 AM - 8 PM) to 24-hour format
// If hour is 1-5, treat it as PM (13-17)
const toSchoolHour24 = (timeStr) => {
  const [h, m] = timeStr.split(":").map(Number);
  const hour24 = h >= 1 && h <= 5 ? h + 12 : h;
  return `${String(hour24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const getSessionStatus = (startTime, endTime, dateStr, now) => {
  if (!startTime || !endTime) return "-";

  const start = moment(
    `${dateStr} ${toSchoolHour24(startTime)}`,
    "YYYY-MM-DD HH:mm",
  );
  const end = moment(
    `${dateStr} ${toSchoolHour24(endTime)}`,
    "YYYY-MM-DD HH:mm",
  );

  if (now.isAfter(end)) return "Ended";
  if (now.isBetween(start, end)) return "In Progress";

  const diffMinutes = start.diff(now, "minutes");
  if (diffMinutes < 0) return "Ended";
  if (diffMinutes < 60) return `Starts in ${diffMinutes}m`;

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return `Starts in ${hours}h ${minutes}m`;
};
